import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { JwtLegFiClaims } from '../../../../../services/auth/jwt-legfi-claims.model';
import { Payable } from '../../../../../models/entities/payable';
import { GrowlerService } from '../../../../shared/growler/growler.service';
import { LegFiJwtService } from '../../../../../services/auth/legfi-jwt.service';
import environment from '../../../../../../environments/environment';
import { Routes } from '../../../../../config/routes';
import { OrganizationFile } from '../../../../../models/entities/organization-file';
import { UploadedFile } from '../../../../../models/entities/uploaded-file';
import { ApplicationHttpClient } from '../../../../shared/http/application-http-client';
import { FileUploaderService } from 'app/services/file-uploader.service';

export interface AttachInvoiceToPayableResult
{
    payable: Payable;
    validFileType: boolean;
}

@Component({
    selector: 'app-upload-invoice-form',
    templateUrl: './upload-invoice-form.component.html',
    styleUrls: ['./upload-invoice-form.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class UploadInvoiceFormComponent implements OnInit
{
    @Input() payable: Payable;
    @Output() invoiceUploadingEvent = new EventEmitter<void>();
    @Output() invoiceUploadedEvent = new EventEmitter<UploadedFile>();
    @Output() invoiceUploadErrorEvent = new EventEmitter<void>();
    fileUploader: FileUploader;
    jwt: JwtLegFiClaims;
    isLoading = false;

    constructor(
            private _http: ApplicationHttpClient,
            private _growler: GrowlerService,
            private _fileUploaderService: FileUploaderService,
    ) {
    }

    ngOnInit(): void {
        this.jwt = LegFiJwtService.read();

        this.prepFileUploader();
    }

    get uploadHint(): string {
        if (!this.payable?.id) {
            return 'This attaches the invoice file to the payable.';
        }

        return this.payable?.invoiceUploadedFileId
            ? 'Uploading a file will attach it as a supporting document.'
            : 'Uploading the file attaches it to this payable as an invoice, but will not overwrite any existing data.';
    }

    prepFileUploader() {
        this.fileUploader = this._fileUploaderService.initUploader({
            // Remove queueLimit to handle single-file logic ourselves
            url: environment.LegFiCoreApi + Routes.LegFiCore.UploadFile(),
            autoUpload: true,
        });

        this.fileUploader.onWhenAddingFileFailed = (item, filter, options) => {
            this._growler.oops('This file could not be added.');
        };

        this.fileUploader.onBeforeUploadItem = (item) => {
            this.isLoading = true;
            this.invoiceUploadingEvent.emit();
        };

        this.fileUploader.onAfterAddingFile = (file): any => {
            // Cancel any existing uploads and clear queue to maintain single file behavior
            if (this.fileUploader.queue.length > 1) {
                // Cancel and remove all items except the newly added one
                const itemsToRemove = this.fileUploader.queue.slice(0, -1);
                itemsToRemove.forEach(item => {
                    if (item.isUploading) {
                        item.cancel();
                    }
                    this.fileUploader.removeFromQueue(item);
                });
            }

            if (file.file.size > environment.WorkerServerFileSizeLimit) {
                this._growler.oops('One of your files exceeds the 100 MB limit.');
                this.clearUploadQueue(file);
            }
            file.file.size = OrganizationFile.convertFileSizeToHumanReadable(file.file.size, 0);
        };

        this.fileUploader.onCompleteItem = (
                item: FileItem,
                response: string,
                status: number,
                headers,
        ) => {
            // Status 0 means cancelled - don't show error for replaced files
            if (status === 0) {
                this.isLoading = false;
                return;
            }

            if (status < 200 || status > 206) {
                if (status === 401) {
                    return this._http.redirectAndThrow401Observable();
                }
                this._growler.oops('There was a problem uploading your file.');
                this.isLoading = false;
                this.clearUploadQueue();
                this.invoiceUploadErrorEvent.emit();
                return;
            }

            const uploadedFile = new UploadedFile(JSON.parse(response)); // Uploaded file object from our api
            this.clearUploadQueue();
            this.isLoading = false;
            return this.invoiceUploadedEvent.emit(uploadedFile);
        };
    }

    clearUploadQueue(fileItem: FileItem = null) {
        if (fileItem) {
            this.fileUploader.removeFromQueue(fileItem);
        } else {
            this.fileUploader.clearQueue();
        }
    }

    // ph-file-upload reports its pick (browse or drop) as a File[] on a composed CustomEvent.
    onFilesPicked(event: Event): void {
        const files = (event as CustomEvent<File[]>).detail;
        if (files && files.length > 0) {
            this.fileUploader.addToQueue([files[0]]);
        }
    }
}
